import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  IconButton,
  CircularProgress,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import MDEditor from '@uiw/react-md-editor';
import { VendorsListingService } from '../../services/vendors.services';
import { CustomFeeModal } from '../../components/Vendors/CustomFeeModal';
import type { Vendor } from '../../types';

// S3 asset URLs
const burgerPlaceholderBig = 'https://eventi-static-assets.s3.me-central-1.amazonaws.com/images/burger-placeholder-big.png';

const VendorProfile: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [vendor, setVendor] = useState<Vendor | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [occasions, setOccasions] = useState<string[]>([]);
  const [categoriesCatalogue, setCategoriesCatalogue] = useState<any>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [feeModalOpen, setFeeModalOpen] = useState(false);
  const [defaultFee, setDefaultFee] = useState<number>(5);

  // Fetch default fee on mount
  useEffect(() => {
    const fetchDefaultFee = async () => {
      try {
        const response = await VendorsListingService.getDefaultFee();
        if (response?.defaultFeePercentage !== undefined) {
          setDefaultFee(response.defaultFeePercentage);
        }
      } catch (error) {
        console.error('Failed to fetch default fee:', error);
      }
    };
    fetchDefaultFee();
  }, []);

  useEffect(() => {
    const fetchVendor = async () => {
      if (!id) {
        setError('Vendor ID is missing');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const vendorData = await VendorsListingService.getVendorById(id);
        
        if (vendorData) {
          setVendor(vendorData);
          setError(null);
          
          // Extract occasions from vendor data if available
          if (vendorData.occasions && Array.isArray(vendorData.occasions)) {
            setOccasions(vendorData.occasions);
          }
          
          // Fetch vendor's categories catalogue
          try {
            const catalogueData = await VendorsListingService.getVendorCategoriesCatalogue(id);
            setCategoriesCatalogue(catalogueData.categoryCatalogue || null);
          } catch (err) {
            console.error('Error fetching categories catalogue:', err);
            setCategoriesCatalogue(null);
          }
        } else {
          setError('Vendor not found');
        }
      } catch (err: any) {
        console.error('Error fetching vendor:', err);
        setError(err.message || 'Failed to load vendor data');
      } finally {
        setLoading(false);
      }
    };

    fetchVendor();
  }, [id]);

  const handleBack = () => {
    navigate('/vendors');
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSuspend = async () => {
    if (!vendor) return;

    try {
      await VendorsListingService.suspendActiveVendor(vendor.vendorId);
      toast.success('Vendor suspended successfully');
      handleMenuClose();
      
      // Refresh vendor data
      const vendorData = await VendorsListingService.getVendorById(vendor.vendorId);
      if (vendorData) {
        setVendor(vendorData);
      }
    } catch (error: any) {
      console.error('Failed to suspend vendor:', error);
      toast.error(error?.response?.data?.message || 'Failed to suspend vendor');
      handleMenuClose();
    }
  };

  const handleActivate = async () => {
    if (!vendor) return;

    try {
      await VendorsListingService.activateSuspendedVendor(vendor.vendorId);
      toast.success('Vendor activated successfully');
      handleMenuClose();
      
      // Refresh vendor data
      const vendorData = await VendorsListingService.getVendorById(vendor.vendorId);
      if (vendorData) {
        setVendor(vendorData);
      }
    } catch (error: any) {
      console.error('Failed to activate vendor:', error);
      toast.error(error?.response?.data?.message || 'Failed to activate vendor');
      handleMenuClose();
    }
  };

  const handleChangeFee = () => {
    setFeeModalOpen(true);
    handleMenuClose();
  };

  const handleFeeModalSuccess = async () => {
    toast.success('Custom fee updated successfully');
    
    // Refresh vendor data
    if (vendor) {
      const vendorData = await VendorsListingService.getVendorById(vendor.vendorId);
      if (vendorData) {
        setVendor(vendorData);
      }
    }
  };

  // Helper Components
  const PropertyRow: React.FC<{ label: string; value: string; isLast?: boolean }> = ({
    label,
    value,
    isLast = false,
  }) => (
    <Box
      sx={{
        display: 'flex',
        alignSelf: 'stretch',
        gap: 'var(--palette-light-1-value)',
        padding: 'var(--palette-light-1-value) 0px',
        borderBottom: isLast ? 'none' : '1px solid var(--palette-light-background-tertiary-value-hex)',
      }}
    >
      <Typography
        sx={{
          flex: 1,
          fontSize: 'var(--palette-light-font-size-default-value)',
          fontWeight: 400,
          lineHeight: 'var(--palette-light-line-height-relaxed-value)',
          color: 'var(--palette-light-text-primary-value-hex)',
        }}
      >
        {label}
      </Typography>
      <Typography
        sx={{
          flex: 1,
          fontSize: 'var(--palette-light-font-size-default-value)',
          fontWeight: 400,
          lineHeight: 'var(--palette-light-line-height-relaxed-value)',
          color: 'var(--palette-light-text-primary-value-hex)',
          whiteSpace: 'pre-line',
        }}
      >
        {value}
      </Typography>
    </Box>
  );

  const SectionHeader: React.FC<{ title: string }> = ({ title }) => (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        alignSelf: 'stretch',
        gap: 'var(--palette-light-4-value)',
      }}
    >
      <Typography
        sx={{
          flex: 1,
          fontSize: 'var(--palette-light-font-size-large-value)',
          fontWeight: 700,
          lineHeight: '1.25',
          letterSpacing: '-1%',
          color: 'var(--palette-light-text-primary-value-hex)',
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  // Show loading state
  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '100vh',
          width: '100%',
          backgroundColor: '#ffffff'
        }}
      >
        <CircularProgress sx={{ color: '#67314f' }} />
      </Box>
    );
  }

  // Show error state
  if (error || !vendor) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          width: '100%',
          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
          backgroundColor: '#ffffff',
          padding: {
            xs: '24px 16px',
            sm: '40px 40px',
            md: '40px 80px',
          },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: 2 }}>
          <IconButton
            onClick={handleBack}
            sx={{
              width: '32px',
              height: '32px',
              padding: 0,
              color: 'var(--palette-light-brand-primary-value-hex)',
            }}
          >
            <ChevronLeftIcon sx={{ fontSize: '16px' }} />
          </IconButton>
          <Typography variant="h1" sx={{ fontSize: '44px', fontWeight: 700, color: '#181818' }}>
            Vendor Not Found
          </Typography>
        </Box>
        <Typography color="error" sx={{ pl: '44px' }}>{error || 'Vendor not found'}</Typography>
      </Box>
    );
  }

  const getLocationsServed = (): string[] => {
    if (vendor.locationsServed && vendor.locationsServed.length > 0) {
      return vendor.locationsServed;
    }
    if (vendor.registeredAddress) {
      return [vendor.registeredAddress];
    }
    return ['Doha, Qatar'];
  };

  const locationsServed = getLocationsServed();

  return (
    <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
        backgroundColor: '#ffffff',
      }}
    >
      <Box
        component="main"
        sx={{
          width: '100%',
          bgcolor: '#ffffff',
          p: 0,
        }}
      >
        {/* Page Title */}
        <Box sx={{ px: 10, py: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '12px', mb: 1 }}>
            <IconButton
              onClick={handleBack}
              sx={{
                width: '32px',
                height: '32px',
                padding: 0,
                color: 'var(--palette-light-brand-primary-value-hex)',
              }}
            >
              <ChevronLeftIcon sx={{ fontSize: '16px' }} />
            </IconButton>
            <Typography 
              variant="h1" 
              sx={{ 
                fontSize: '44px',
                fontWeight: 700,
                color: '#181818',
                lineHeight: 1.15,
                letterSpacing: '-0.44px',
                flex: 1,
              }}
            >
              {vendor.companyName || 'Vendor Profile'}
            </Typography>
            {/* Action Menu Button */}
            <IconButton
              onClick={handleMenuOpen}
              sx={{
                width: '40px',
                height: '40px',
                color: '#181818',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.04)',
                },
              }}
            >
              <MoreVertIcon sx={{ fontSize: '24px' }} />
            </IconButton>
          </Box>
          <Typography 
            sx={{ 
              fontSize: '16px',
              fontWeight: 400,
              color: '#181818',
              lineHeight: 1.4,
              pl: '44px'
            }}
          >
            View vendor profile information and settings
          </Typography>
        </Box>

        {/* Main Content */}
        <Box sx={{ px: 10, pb: 5 }}>
          <Box sx={{ 
            display: 'flex', 
            gap: 5,
            alignItems: 'flex-start'
          }}>
            {/* Left Column */}
            <Box sx={{ flex: 1, maxWidth: '500px' }}>
              {/* Company Image */}
              <Box sx={{ mb: 4 }}>
                <Box sx={{ position: 'relative', mb: 2.5 }}>
                  <Box
                    sx={{
                      height: 175,
                      backgroundImage: `url(${vendor.profileImageUrl || burgerPlaceholderBig})`,
                      backgroundSize: 'cover',
                      backgroundPosition: 'center',
                      borderRadius: '12px',
                      border: '1px solid #c5cbd3'
                    }}
                  />
                </Box>
              </Box>

              {/* Primary Info */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  gap: 'var(--palette-light-4-value)',
                  mb: 4,
                }}
              >
                <SectionHeader title={vendor.companyName || 'Vendor Information'} />
                <Typography
                  sx={{
                    fontSize: 'var(--palette-light-font-size-default-value)',
                    fontWeight: 400,
                    lineHeight: 'var(--palette-light-line-height-relaxed-value)',
                    color: 'var(--palette-light-text-primary-value-hex)',
                  }}
                >
                  {vendor.companyDescription || 'No description available'}
                </Typography>

                {vendor.companyWebsite && (
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      sx={{
                        fontSize: 'var(--palette-light-font-size-default-value)',
                        fontWeight: 400,
                        lineHeight: 'var(--palette-light-line-height-relaxed-value)',
                        color: 'var(--palette-light-text-primary-value-hex)',
                      }}
                    >
                      Website: {vendor.companyWebsite}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Contact Information */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  gap: 'var(--palette-light-4-value)',
                  mb: 4,
                }}
              >
                <SectionHeader title="Contact Information" />
                <Box>
                  <PropertyRow 
                    label="Phone:" 
                    value={vendor.phone && vendor.countryCode 
                      ? `${vendor.countryCode} ${vendor.phone}`
                      : vendor.phone || 'Not provided'
                    } 
                  />
                  <PropertyRow 
                    label="Email:" 
                    value={vendor.email || 'Not provided'} 
                  />
                  <PropertyRow 
                    label="Contact Person:" 
                    value={vendor.contactName || 'Not provided'} 
                    isLast 
                  />
                </Box>
              </Box>

              {/* Service Category and Items */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  gap: 'var(--palette-light-4-value)',
                  mb: 4,
                }}
              >
                <SectionHeader title="Service Category and Items" />
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignSelf: 'stretch',
                  }}
                >
                  {categoriesCatalogue && categoriesCatalogue.mainCategory ? (
                    <>
                      <PropertyRow
                        label="Main Category"
                        value={categoriesCatalogue.mainCategory.name}
                      />
                      {categoriesCatalogue.subCategories && categoriesCatalogue.subCategories.length > 0 && (
                        <PropertyRow
                          label="Sub Categories"
                          value={categoriesCatalogue.subCategories.map((sub: any) => sub.name).join(', ')}
                        />
                      )}
                      {categoriesCatalogue.types && categoriesCatalogue.types.length > 0 && (
                        <PropertyRow
                          label="Service Types"
                          value={categoriesCatalogue.types.map((type: any) => type.name).join(', ')}
                        />
                      )}
                      {categoriesCatalogue.items && categoriesCatalogue.items.length > 0 && (
                        <PropertyRow
                          label="Service Items"
                          value={categoriesCatalogue.items.map((item: any) => item.name).join(', ')}
                          isLast
                        />
                      )}
                    </>
                  ) : vendor.categoryTreeEnriched && vendor.categoryTreeEnriched.mainCategoryName ? (
                    <>
                      <PropertyRow
                        label="Main Category"
                        value={vendor.categoryTreeEnriched.mainCategoryName}
                      />
                      {vendor.categoryTreeEnriched.subCategoryNames && vendor.categoryTreeEnriched.subCategoryNames.length > 0 && (
                        <PropertyRow
                          label="Sub Categories"
                          value={vendor.categoryTreeEnriched.subCategoryNames.join(', ')}
                        />
                      )}
                      {vendor.categoryTreeEnriched.typeNames && vendor.categoryTreeEnriched.typeNames.length > 0 && (
                        <PropertyRow
                          label="Service Types"
                          value={vendor.categoryTreeEnriched.typeNames.join(', ')}
                        />
                      )}
                      {vendor.categoryTreeEnriched.itemNames && vendor.categoryTreeEnriched.itemNames.length > 0 && (
                        <PropertyRow
                          label="Service Items"
                          value={vendor.categoryTreeEnriched.itemNames.join(', ')}
                          isLast
                        />
                      )}
                    </>
                  ) : (
                    <Box
                      sx={{
                        padding: 'var(--palette-light-1-value) 0px',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: 'var(--palette-light-font-size-default-value)',
                          fontWeight: 400,
                          color: 'var(--palette-light-text-secondary-value-hex)',
                          fontStyle: 'italic',
                        }}
                      >
                        Vendor has not completed service selection
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Occasions */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  gap: 'var(--palette-light-4-value)',
                  mb: 4,
                }}
              >
                <SectionHeader title="Occasions" />
                <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1, display: 'flex', justifyContent: 'space-between' }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#181818', 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: '16px',
                      fontWeight: 400
                    }}
                  >
                    Selected Occasions:
                  </Typography>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#181818', 
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: '16px',
                      fontWeight: 400
                    }}
                  >
                    {occasions.length > 0 ? occasions.slice(0, 3).join(', ') : 'None'}
                  </Typography>
                </Box>
              </Box>

              {/* Locations Served */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  gap: 'var(--palette-light-4-value)',
                  mb: 4,
                }}
              >
                <SectionHeader title="Locations served" />
                <Box sx={{ borderBottom: '1px solid #f1f4f4', py: 1 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      color: '#181818',
                      fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                      fontSize: '16px',
                      fontWeight: 400
                    }}
                  >
                    {locationsServed.join(', ')}
                  </Typography>
                </Box>
              </Box>
            </Box>

              {/* Shipping & Fee Information */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  gap: 'var(--palette-light-4-value)',
                  mb: 4,
                }}
              >
                <SectionHeader title="Shipping & Fee Information" />
                <Box>
                  <PropertyRow 
                    label="Shipping Fee (Inside Doha):" 
                    value={vendor.shippingFeeInsideDoha !== undefined ? `QAR ${vendor.shippingFeeInsideDoha}` : 'Not specified'} 
                  />
                  <PropertyRow 
                    label="Shipping Fee (Outside Doha):" 
                    value={vendor.shippingFeeOutsideDoha !== undefined ? `QAR ${vendor.shippingFeeOutsideDoha}` : 'Not specified'} 
                    isLast
                  />
                </Box>
              </Box>

            {/* Right Column */}
            <Box sx={{ flex: 1, maxWidth: '500px' }}>
              {/* Legal Information */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  gap: 'var(--palette-light-4-value)',
                  mb: 4,
                }}
              >
                <SectionHeader title="Legal Information" />
                <Box>
                  <PropertyRow 
                    label="Trade License Number:" 
                    value={vendor.tradeLicenseNumber || 'Not provided'} 
                  />
                  <PropertyRow 
                    label="Commercial Registration No.:" 
                    value={vendor.commercialRegistrationNo || 'Not provided'} 
                  />
                  <PropertyRow 
                    label="Registered Name:" 
                    value={vendor.registeredName || 'Not provided'} 
                  />
                  <PropertyRow 
                    label="Registered Address:" 
                    value={vendor.registeredAddress || 'Not provided'} 
                    isLast 
                  />
                </Box>
              </Box>

              {/* Service Policy */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  gap: 'var(--palette-light-4-value)',
                  mb: 4,
                }}
              >
                <SectionHeader title="Service Policy" />
                <Box>
                  <PropertyRow 
                    label="Accept orders not later than:" 
                    value={vendor.acceptOrdersDeadline !== undefined ? `${vendor.acceptOrdersDeadline} hours` : 'Not specified'} 
                  />
                  <PropertyRow 
                    label="Accept changes not later than:" 
                    value={vendor.acceptChangesDeadline !== undefined ? `${vendor.acceptChangesDeadline} hours` : 'Not specified'} 
                  />
                  <Box sx={{ py: 1 }}>
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: '#181818', 
                        fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                        fontSize: '16px',
                        fontWeight: 400,
                        mb: 1
                      }}
                    >
                      Policy:
                    </Typography>
                    {vendor.servicePolicy ? (
                      <Box
                        data-color-mode="light"
                        sx={{
                          '& .w-md-editor': {
                            backgroundColor: 'transparent',
                            border: 'none',
                            boxShadow: 'none',
                            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                            '& .w-md-editor-bar': {
                              display: 'none',
                            },
                          },
                          '& .w-md-editor-text': {
                            display: 'none',
                          },
                          '& .w-md-editor-preview': {
                            backgroundColor: 'transparent',
                            padding: 0,
                            border: 'none',
                            fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                            fontSize: '16px',
                            color: '#181818',
                            '& p': {
                              margin: 0,
                              marginBottom: '8px',
                              fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                              fontSize: '16px',
                              color: '#181818',
                              '&:last-child': {
                                marginBottom: 0,
                              },
                            },
                          }
                        }}
                      >
                        <MDEditor
                          value={String(vendor.servicePolicy || '')}
                          preview="preview"
                          hideToolbar={true}
                          visibleDragbar={false}
                        />
                      </Box>
                    ) : (
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          color: '#181818', 
                          fontFamily: '"HT Mosafer Rounded", "Roboto", "Helvetica", "Arial", sans-serif',
                          fontSize: '16px',
                          fontWeight: 400,
                        }}
                      >
                        No service policy provided
                      </Typography>
                    )}
                  </Box>
                </Box>
              </Box>

              {/* Bank Account Information */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  gap: 'var(--palette-light-4-value)',
                  mb: 4,
                }}
              >
                <SectionHeader title="Bank Account Information" />
                <Box>
                  <PropertyRow 
                    label="Bank Name:" 
                    value={vendor.bankName || 'Not provided'} 
                  />
                  <PropertyRow 
                    label="Account Name:" 
                    value={vendor.bankAccountName || 'Not provided'} 
                  />
                  <PropertyRow 
                    label="Account Number:" 
                    value={vendor.bankAccountNumber || 'Not provided'} 
                    isLast
                  />
                </Box>
              </Box>

              {/* Account Information (Admin-specific) */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignSelf: 'stretch',
                  gap: 'var(--palette-light-4-value)',
                  mb: 4,
                }}
              >
                <SectionHeader title="Account Information" />
                <Box>
                  <PropertyRow 
                    label="Vendor ID:" 
                    value={vendor.vendorId} 
                  />
                  <PropertyRow 
                    label="Status:" 
                    value={vendor.status ? vendor.status.charAt(0).toUpperCase() + vendor.status.slice(1) : 'Unknown'} 
                  />
                  <PropertyRow 
                    label="Created:" 
                    value={new Date(vendor.vendorCreatedAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    })} 
                  />
                  {vendor.eventiFeePercentage !== undefined && (
                    <PropertyRow 
                      label="Eventi Fee:" 
                      value={`${vendor.eventiFeePercentage}%`} 
                    />
                  )}
                  <PropertyRow 
                    label="Rating:" 
                    value={vendor.rating ? `${vendor.rating} / 5` : 'No rating yet'} 
                  />
                  <PropertyRow 
                    label="Total Orders:" 
                    value={vendor.orderCount?.toString() || '0'} 
                  />
                  <PropertyRow 
                    label="Total Earnings:" 
                    value={vendor.totalEarnings ? `QAR ${vendor.totalEarnings.toFixed(2)}` : 'QAR 0.00'} 
                  />
                  <PropertyRow 
                    label="Reviews:" 
                    value={vendor.reviewsCount?.toString() || '0'} 
                  />
                  <PropertyRow 
                    label="Questions:" 
                    value={vendor.questionsCount?.toString() || '0'} 
                    isLast 
                  />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {/* Action Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {vendor.status?.toLowerCase() === 'inreview' ? (
          // For InReview status, show both Activate and Suspend
          <>
            <MenuItem onClick={handleActivate}>Activate</MenuItem>
            <MenuItem onClick={handleSuspend}>Suspend</MenuItem>
          </>
        ) : vendor.status?.toLowerCase() === 'suspended' ? (
          // For Suspended status, show only Activate
          <MenuItem onClick={handleActivate}>Activate</MenuItem>
        ) : (
          // For Active or other status, show only Suspend
          <MenuItem onClick={handleSuspend}>Suspend</MenuItem>
        )}
        <MenuItem onClick={handleChangeFee}>Change fee amount</MenuItem>
      </Menu>

      {/* Custom Fee Modal */}
      {vendor && (
        <CustomFeeModal
          open={feeModalOpen}
          onClose={() => setFeeModalOpen(false)}
          vendor={{
            vendorId: vendor.vendorId,
            companyName: vendor.companyName || 'Unknown Vendor',
            customFeePercentage: vendor.customFeePercentage,
          }}
          defaultFee={defaultFee}
          onSuccess={handleFeeModalSuccess}
        />
      )}
    </Box>
  );
};

export default VendorProfile;
